import type { UpdateAnalyticsCachesInWorker, MailboxAccountInfoMappingAddon, } from "owa-analytics-types";
import { updateCommonPropertiesCache } from "owa-analytics-core/lib/settings/getCommonProperties";
import { updateFlightsAndApplicationSettingsCache } from "owa-analytics-core/lib/settings/getAnalyticsFlightsAndAppSettings";
import { updatePartialCachedSettings } from "owa-analytics-core/lib/settings/getCachedSettings";
import { getAnalyticsAddon } from "owa-analytics-addons";
import { trace } from "owa-trace";

export function updateAnalyticsCachesInWorker(cachesToUpdate: UpdateAnalyticsCachesInWorker): void {
    const accountKey = cachesToUpdate.accountKey ?? "globalAccount";
    const alternateAccountKey = cachesToUpdate.alternateAccountKey;
    
    trace.info(`[AnalyticsCacheSync] Worker received cache update. accountKey=${accountKey} alternateAccountKey=${alternateAccountKey ?? "none"} mailboxCollectorUrlMappingsCount=${cachesToUpdate.mailboxCollectorUrlMappings?.length} hasFlights=${!!cachesToUpdate.FlightsAndApplicationSettingsCache} hasUserConfiguration=${!!cachesToUpdate.UserConfigurationCache} hasCommonProperties=${!!cachesToUpdate.CommonPropertiesCache} hasEcsETag=${!!cachesToUpdate.EcsETagCache}`, "analytics");
    
    if (cachesToUpdate.mailboxCollectorUrlMappings?.length) {
        const mailboxMappingAddon = getAnalyticsAddon<MailboxAccountInfoMappingAddon>("MailboxAccountInfoMapping")?.executeNow();
        if (mailboxMappingAddon) {
            for (const { accountKey: mappingAccountKey, collectorUrl, multiAccountType, mailboxType, } of cachesToUpdate.mailboxCollectorUrlMappings) {
                mailboxMappingAddon.setMailboxCollectorUrlMapping(mappingAccountKey, collectorUrl, multiAccountType, mailboxType);
            }
        }
    }
    
    // Update the FlightsAndApplicationSettings Cache
    if (cachesToUpdate.FlightsAndApplicationSettingsCache) {
        updateFlightsAndApplicationSettingsCache(cachesToUpdate.FlightsAndApplicationSettingsCache, accountKey);
        
        if (alternateAccountKey) {
            updateFlightsAndApplicationSettingsCache(cachesToUpdate.FlightsAndApplicationSettingsCache, alternateAccountKey);
        }
    }
    
    // Update the UserConfiguration Cache
    if (cachesToUpdate.UserConfigurationCache) {
        updatePartialCachedSettings(accountKey, {
            userConfiguration: cachesToUpdate.UserConfigurationCache,
        });
        
        if (alternateAccountKey) {
            updatePartialCachedSettings(alternateAccountKey, {
                userConfiguration: cachesToUpdate.UserConfigurationCache,
            });
        }
    }
    
    // Update the CommonProperties Cache
    if (cachesToUpdate.CommonPropertiesCache) {
        updateCommonPropertiesCache(cachesToUpdate.CommonPropertiesCache, accountKey);
        
        if (alternateAccountKey) {
            updateCommonPropertiesCache(cachesToUpdate.CommonPropertiesCache, alternateAccountKey);
        }
    }
    
    // Update the ECS ETag in the analytics-cached-settings cache so the
    // stamping path on this worker thread can read it via getCachedSettings().
    if (cachesToUpdate.EcsETagCache) {
        const snapshot = cachesToUpdate.EcsConfigStateCache;
        const ecsConfigState = snapshot?.etag === cachesToUpdate.EcsETagCache &&
            (accountKey === "globalAccount" || snapshot.accountKey === accountKey) &&
            (!alternateAccountKey ||
                alternateAccountKey === "globalAccount" ||
                snapshot.accountKey === alternateAccountKey)
            ? snapshot
            : undefined;
        if (cachesToUpdate.EcsConfigStateCache && !ecsConfigState) {
            trace.warn("Ignored an incoherent config-state worker cache snapshot.", "analytics");
        }
        updatePartialCachedSettings(accountKey, {
            ecsETag: cachesToUpdate.EcsETagCache,
            ecsConfigState,
        });
        
        if (alternateAccountKey) {
            updatePartialCachedSettings(alternateAccountKey, {
                ecsETag: cachesToUpdate.EcsETagCache,
                ecsConfigState,
            });
        }
    }
    
    trace.info(`[AnalyticsCacheSync] Worker cache update applied. accountKey=${accountKey} alternateAccountKey=${alternateAccountKey ?? "none"}`, "analytics");
}
